'use client';

import Link from 'next/link';
import {Button} from '@/components/ui/button';
import {Heading} from '@/components/heading';
import {Body} from '@/components/body';
import CheckFat from '@/assets/icons/CheckFat.svg';
import SectionHeader from '@/app/(website)/components/SectionHeader';
import {useEffect, useRef, useState} from 'react';
import QuotationFormSheet from '@/app/(website)/components/QuotationFormSheet';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs';

export default function PricingLegal() {
  const [isVisible, setIsVisible] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2,
      },
    );

    if (gridRef.current) {
      observer.observe(gridRef.current);
    }

    return () => {
      if (gridRef.current) {
        observer.unobserve(gridRef.current);
      }
    };
  }, []);

  return (
    <div id="pricing" className="w-full md:px-8 py-16">
      <div className="bg-background-2 flex flex-col items-center justify-center md:rounded-2xl px-4 py-16 gap-16">
        <SectionHeader
          badgeText="PIANI TARIFFARI"
          title={
            <>
              Piani{' '}
              <span className="rounded-[2rem] rounded-br-[8px] py-0 px-2 md:px-4 bg-secondary text-primary">
                flessibili
              </span>{' '}
              per il singolo professionista e per lo studio
            </>
          }
          subtitle="Scegli la formula più adatta al tuo modo di lavorare. Nessun vincolo complesso, piena continuità operativa."
          titleAnimationStart="bottom"
        />
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-[82rem]"
          ref={gridRef}
        >
          <div
            className={`bg-white rounded-2xl flex flex-col justify-between px-8 py-12 gap-8 transition-all duration-1000 ease-in-out transform ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-40'
            }`}
          >
            <div className="flex flex-col items-center gap-4">
              <div className="text-primary text-2xl font-bold text-center">
                Abbonamento “Plus” Singolo
              </div>
              <Tabs
                defaultValue="yearly"
                className="w-full flex flex-col items-center gap-2"
              >
                <TabsList className="mx-auto h-14 rounded-2xl">
                  <TabsTrigger
                    value="monthly"
                    className="data-[state=active]:bg-secondary data-[state=active]:text-white text-lg h-full px-4 md:px-8 rounded-2xl"
                  >
                    Mensile
                  </TabsTrigger>
                  <TabsTrigger
                    value="yearly"
                    className="data-[state=active]:bg-secondary data-[state=active]:text-white text-lg h-full px-4 md:px-8 rounded-2xl"
                  >
                    Annuale
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="monthly">
                  <div className="flex items-baseline gap-2">
                    <Heading variant="h3">€99/mese</Heading>
                    <Heading
                      variant="h5"
                      className="text-grey-3 font-recoleta-alt"
                    >
                      oltre IVA
                    </Heading>
                  </div>
                </TabsContent>
                <TabsContent value="yearly">
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-baseline gap-2">
                      <Heading variant="h3">€80/mese</Heading>
                      <Heading
                        variant="h5"
                        className="text-grey-3 font-recoleta-alt"
                      >
                        oltre IVA
                      </Heading>
                    </div>
                    <Body className="text-center">
                      PROMO Nuovi Clienti
                      <br />
                      Abbonamento Plus Annuale con pagamento mensilizzato.
                    </Body>
                  </div>
                </TabsContent>
              </Tabs>
              <div className="flex flex-col gap-8">
                <div className="flex flex-col w-full gap-4">
                  <div className="flex flex-row gap-4">
                    <div className="shrink-0">
                      <CheckFat width={24} height={24} />
                    </div>
                    <Body variant="bold">
                      Pensato per avvocato singolo, collaboratore o
                      professionista che vuole uno strumento operativo personale
                    </Body>
                  </div>
                  <div className="flex flex-row gap-4">
                    <div>
                      <CheckFat width={24} height={24} />
                    </div>
                    <Body variant="bold">Dispositivi illimitati</Body>
                  </div>
                  <div className="flex flex-row gap-4">
                    <div>
                      <CheckFat width={24} height={24} />
                    </div>
                    <Body variant="bold">
                      Tutte le fonti comprese e costantemente aggiornate
                    </Body>
                  </div>
                  <div className="flex flex-row gap-4">
                    <div>
                      <CheckFat width={24} height={24} />
                    </div>
                    <Body variant="bold">Caricamento documentale</Body>
                  </div>
                  <div className="flex flex-row gap-4">
                    <div>
                      <CheckFat width={24} height={24} />
                    </div>
                    <Body variant="bold">
                      Chat completa di tutte le funzionalità “Plus”
                      <ul className="list-disc list-inside">
                        <li>Ragionamento Avanzato "N2"</li>
                        <li>Tutor Prompt</li>
                        <li>Personalizza Documenti</li>
                        <li>Personalizza Fonti</li>
                      </ul>
                    </Body>
                  </div>
                  <div className="flex flex-row gap-4">
                    <div>
                      <CheckFat width={24} height={24} />
                    </div>
                    <Body variant="bold">Assistenza dedicata</Body>
                  </div>
                </div>
              </div>
            </div>
            <Link
              href={`${process.env.NEXT_PUBLIC_NAI_APP_URL}/sign-up`}
              className="flex"
            >
              <Button className="flex-1">Attiva la Prova Gratuita</Button>
            </Link>
          </div>
          <div
            className={`bg-white rounded-2xl flex flex-col justify-between px-8 py-12 gap-8 transition-all duration-1000 ease-in-out transform delay-500 ${
              isVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-40'
            }`}
          >
            <div className="flex-1 flex flex-col items-center gap-4">
              <div className="text-primary text-2xl font-bold text-center">
                Abbonamento “Plus” Multiutente
              </div>
              <div className="w-full flex-1 flex flex-col items-center justify-center gap-2">
                <div className="flex flex-col items-center gap-2">
                  <Heading variant="h3">
                    A partire da €49/mese ad utente
                  </Heading>
                  <Heading
                    variant="h5"
                    className="text-grey-3 font-recoleta-alt"
                  >
                    oltre IVA
                  </Heading>
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col w-full gap-4">
                    <div className="flex flex-row gap-4">
                      <div className="shrink-0">
                        <CheckFat width={24} height={24} />
                      </div>
                      <Body variant="bold">
                        Pensato per studi legali, team verticali, boutique e
                        strutture con partner, associati, collaboratori o
                        praticanti
                      </Body>
                    </div>
                    <div className="flex flex-row gap-4">
                      <div>
                        <CheckFat width={24} height={24} />
                      </div>
                      <Body variant="bold">
                        Dispositivi illimitati per ogni utente
                      </Body>
                    </div>
                    <div className="flex flex-row gap-4">
                      <div>
                        <CheckFat width={24} height={24} />
                      </div>
                      <Body variant="bold">
                        Tutte le fonti comprese e costantemente aggiornate
                      </Body>
                    </div>
                    <div className="flex flex-row gap-4">
                      <div>
                        <CheckFat width={24} height={24} />
                      </div>
                      <Body variant="bold">Caricamento documentale</Body>
                    </div>
                    <div className="flex flex-row gap-4">
                      <div>
                        <CheckFat width={24} height={24} />
                      </div>
                      <Body variant="bold">
                        Chat completa di tutte le funzionalità “Plus”
                        <ul className="list-disc list-inside">
                          <li>Ragionamento Avanzato "N2"</li>
                          <li>Tutor Prompt</li>
                          <li>Personalizza Documenti</li>
                          <li>Personalizza Fonti</li>
                        </ul>
                      </Body>
                    </div>
                    <div className="flex flex-row gap-4">
                      <div>
                        <CheckFat width={24} height={24} />
                      </div>
                      <Body variant="bold">Assistenza dedicata</Body>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <QuotationFormSheet />
          </div>
        </div>
      </div>
    </div>
  );
}
